import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { User, Plus, Star, Trash2, X, RefreshCw, DollarSign, Clock, Link as LinkIcon, Send } from 'lucide-react';
import { toast } from 'sonner';
import { SwissCard, InlineNumber, InlineSelect } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;
const VEHICLES = ['Concierge SUV', 'Concierge Sedan', 'Sprinter Van', 'Rolls-Royce Cullinan', 'Rolls-Royce Ghost', 'Maybach GLS', 'Maybach Sedan', 'Lamborghini Urus', 'Bentley Bentayga', 'Bentley Flying Spur', 'Lamborghini Huracan'];

const DriverRosterTab = () => {
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showTripForm, setShowTripForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', primary_vehicle: '', pay_type: 'hourly', rate: 35, license: '' });
  const [tripForm, setTripForm] = useState({ date: '', vehicle: '', client: '', hours: 0, revenue: 0, payout: 0, rating: 5, notes: '' });

  const load = () => { fetch(`${API}/api/command-center/drivers`).then(r => r.json()).then(d => setDrivers(d.drivers || [])); };
  useEffect(load, []);

  const createDriver = async () => {
    if (!form.name) return;
    await fetch(`${API}/api/command-center/drivers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setShowForm(false); setForm({ name: '', phone: '', email: '', primary_vehicle: '', pay_type: 'hourly', rate: 35, license: '' }); load();
  };

  const deleteDriver = async (id) => {
    if (!window.confirm('Delete this driver?')) return;
    await fetch(`${API}/api/command-center/drivers/${id}`, { method: 'DELETE' }); load(); setSelectedDriver(null);
  };

  const toggleAvailability = async (d) => {
    const newStatus = d.status === 'available' ? 'unavailable' : 'available';
    await fetch(`${API}/api/command-center/drivers/${d.driver_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) }); load();
  };

  const updateDriver = async (driver_id, patch) => {
    await fetch(`${API}/api/command-center/drivers/${driver_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
    load();
    toast.success('Driver updated');
  };

  const logTrip = async () => {
    if (!selectedDriver) return;
    await fetch(`${API}/api/command-center/drivers/${selectedDriver.driver_id}/trips`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(tripForm) });
    setShowTripForm(false); setTripForm({ date: '', vehicle: '', client: '', hours: 0, revenue: 0, payout: 0, rating: 5, notes: '' }); load();
  };

  const stats = { total: drivers.length, available: drivers.filter(d => d.status === 'available').length, trips: drivers.reduce((s, d) => s + (d.total_trips || 0), 0), paid: drivers.reduce((s, d) => s + (d.total_earned || 0), 0) };
  const avgRating = (d) => { if (!d.trips?.length) return '—'; return (d.trips.reduce((s, t) => s + (t.rating || 5), 0) / d.trips.length).toFixed(1); };

  const clockLinkFor = (d) => `${window.location.origin}/driver/${d.driver_id}/clock`;

  const copyClockLink = async (d, e) => {
    e?.stopPropagation();
    const url = clockLinkFor(d);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Clock-in link copied for ${d.name}`);
    } catch {
      toast.error('Could not copy. Link: ' + url);
    }
  };

  const smsClockLink = async (d, e) => {
    e?.stopPropagation();
    if (!d.phone) { toast.error(`${d.name} has no phone on file. Add one first.`); return; }
    const url = clockLinkFor(d);
    const msg = `ACE Driver Clock-In · Tap to clock in/out at the start & end of each shift. Bookmark this link on your phone: ${url}`;
    try {
      const r = await fetch(`${API}/api/command-center/quo/send-sms`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: d.phone, message: msg }),
      });
      const data = await r.json();
      if (data.success) {
        toast.success(`Clock-in link sent to ${d.name} via SMS`);
      } else {
        toast.error('SMS failed to send. Link copied instead.');
        await copyClockLink(d);
      }
    } catch {
      toast.error('SMS error. Link copied instead.');
      copyClockLink(d);
    }
  };

  return (
    <div data-testid="driver-roster-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><User className="h-5 w-5 text-red-600" /> Driver Roster</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="text-xs h-8"><RefreshCw className="h-3 w-3 mr-1" /> Refresh</Button>
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-xs h-8" data-testid="add-driver-btn"><Plus className="h-3 w-3 mr-1" /> Add Driver</Button>
        </div>
      </div>

      {/* Add Driver Form */}
      {showForm && (
        <Card className="p-4 border-gray-200 mb-4">
          <div className="flex justify-between mb-3"><h3 className="text-sm font-bold">Add Driver</h3><button onClick={() => setShowForm(false)}><X className="h-4 w-4 text-gray-400" /></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Input placeholder="Full Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-9 text-xs" data-testid="driver-name-input" />
            <Input placeholder="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="h-9 text-xs" />
            <Input placeholder="Email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="h-9 text-xs" />
            <Select value={form.primary_vehicle} onValueChange={v => setForm(p => ({ ...p, primary_vehicle: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Primary Vehicle" /></SelectTrigger><SelectContent>{VEHICLES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
            <div className="flex gap-1">
              {['hourly', 'flat', 'pct'].map(t => (
                <button key={t} onClick={() => setForm(p => ({ ...p, pay_type: t }))} className={`flex-1 h-9 text-[10px] font-bold rounded border ${form.pay_type === t ? 'bg-black text-white border-black' : 'border-gray-200'}`}>
                  {t === 'hourly' ? 'Hourly' : t === 'flat' ? 'Flat/Trip' : '% Trip'}
                </button>
              ))}
            </div>
            <Input type="number" placeholder={form.pay_type === 'pct' ? 'Rate %' : 'Rate $'} value={form.rate} onChange={e => setForm(p => ({ ...p, rate: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
            <Input placeholder="License #" value={form.license} onChange={e => setForm(p => ({ ...p, license: e.target.value }))} className="h-9 text-xs" />
          </div>
          <div className="flex gap-2 mt-3"><Button size="sm" onClick={createDriver} className="bg-red-600 hover:bg-red-700 text-xs h-8">Save Driver</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="text-xs h-8">Cancel</Button></div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-gray-900">{stats.total}</p><p className="text-[10px] text-gray-500">Total Drivers</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-emerald-600">{stats.available}</p><p className="text-[10px] text-gray-500">Available Today</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-gray-900">{stats.trips}</p><p className="text-[10px] text-gray-500">Total Trips</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-red-600">{f(stats.paid)}</p><p className="text-[10px] text-gray-500">Total Paid Out</p></Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Driver List */}
        <div className="lg:col-span-2">
          {drivers.length === 0 ? (
            <Card className="p-8 text-center border-gray-200"><User className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">No drivers added yet.</p></Card>
          ) : (
            <div className="space-y-2">
              {drivers.map(d => (
                <SwissCard key={d.driver_id} className={`p-3 cursor-pointer transition-all ${selectedDriver?.driver_id === d.driver_id ? 'border-[#0A0A0A] shadow-[4px_4px_0px_#0A0A0A]' : 'hover:border-[#0A0A0A]'}`}>
                  <div className="flex items-center justify-between" onClick={() => setSelectedDriver(d)}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center text-xs font-black">{d.name?.charAt(0) || 'D'}</div>
                      <div>
                        <p className="text-sm font-bold text-[#0A0A0A]">{d.name}</p>
                        <div className="flex items-center gap-2 text-[10px] text-[#666]" onClick={e => e.stopPropagation()}>
                          <span>{d.primary_vehicle || '—'}</span>
                          <span>·</span>
                          <InlineSelect
                            value={d.pay_type}
                            options={[{ value: 'hourly', label: 'Hourly' }, { value: 'flat', label: 'Flat/Trip' }, { value: 'pct', label: 'Percent' }]}
                            onChange={(v) => updateDriver(d.driver_id, { pay_type: v })}
                            renderTrigger={(v) => <span className="text-[10px] font-bold uppercase tracking-wider underline decoration-dotted">{v}</span>}
                            testid={`driver-paytype-${d.driver_id}`}
                          />
                          <InlineNumber
                            value={d.rate || 0}
                            onChange={(v) => updateDriver(d.driver_id, { rate: v })}
                            prefix={d.pay_type === 'pct' ? '' : '$'}
                            suffix={d.pay_type === 'pct' ? '%' : d.pay_type === 'flat' ? '/trip' : '/hr'}
                            testid={`driver-rate-${d.driver_id}`}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs font-semibold">{d.total_trips || 0} trips</p>
                        <p className="text-[10px] text-gray-500">{f(d.total_earned || 0)} earned</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs"><Star className="h-3 w-3 text-amber-500" />{avgRating(d)}</div>
                      <button onClick={e => copyClockLink(d, e)} title="Copy mobile clock-in link" data-testid={`copy-clock-link-${d.driver_id}`}
                        className="px-2 py-1 rounded text-[9px] font-bold bg-gray-900 text-white hover:bg-black inline-flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" /> Link
                      </button>
                      <button onClick={e => smsClockLink(d, e)} title="Send clock-in link via SMS" data-testid={`sms-clock-link-${d.driver_id}`}
                        className="px-2 py-1 rounded text-[9px] font-bold bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1">
                        <Send className="h-3 w-3" /> SMS
                      </button>
                      <button onClick={e => { e.stopPropagation(); toggleAvailability(d); }}
                        className={`px-2 py-1 rounded text-[9px] font-bold ${d.status === 'available' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {d.status === 'available' ? 'Available' : 'Off'}
                      </button>
                    </div>
                  </div>
                </SwissCard>
              ))}
            </div>
          )}
        </div>

        {/* Driver Detail / Trip Log */}
        <div>
          {selectedDriver ? (
            <Card className="p-4 border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold">{selectedDriver.name}</h3>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => setShowTripForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-[10px] h-7 px-2"><Plus className="h-3 w-3 mr-0.5" /> Log Trip</Button>
                  <Button size="sm" variant="outline" onClick={() => deleteDriver(selectedDriver.driver_id)} className="text-[10px] h-7 px-2 text-red-500"><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-2 bg-gray-50 rounded text-center"><p className="text-[9px] text-gray-500">Trips</p><p className="text-sm font-bold">{selectedDriver.total_trips || 0}</p></div>
                <div className="p-2 bg-gray-50 rounded text-center"><p className="text-[9px] text-gray-500">Earned</p><p className="text-sm font-bold text-red-600">{f(selectedDriver.total_earned || 0)}</p></div>
                <div className="p-2 bg-gray-50 rounded text-center"><p className="text-[9px] text-gray-500">Rating</p><p className="text-sm font-bold">{avgRating(selectedDriver)}</p></div>
                <div className="p-2 bg-gray-50 rounded text-center"><p className="text-[9px] text-gray-500">Vehicle</p><p className="text-[10px] font-semibold">{selectedDriver.primary_vehicle || '—'}</p></div>
              </div>
              <div className="text-[10px] text-gray-500 mb-3 space-y-0.5">
                <p>{selectedDriver.phone}</p><p>{selectedDriver.email}</p>{selectedDriver.license && <p>License: {selectedDriver.license}</p>}
              </div>

              {/* Mobile Clock-In Link */}
              <div className="p-2.5 mb-3 bg-gradient-to-r from-gray-900 to-black rounded-lg" data-testid="driver-clock-link-card">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[9px] font-bold text-red-400 uppercase tracking-widest">Mobile Clock-In Link</p>
                  <Clock className="h-3 w-3 text-gray-500" />
                </div>
                <p className="text-[10px] text-gray-300 mb-2 font-mono truncate">/driver/{selectedDriver.driver_id}/clock</p>
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={() => copyClockLink(selectedDriver)} className="flex-1 bg-white text-black hover:bg-gray-100 text-[10px] h-7" data-testid="detail-copy-clock-link">
                    <LinkIcon className="h-3 w-3 mr-1" /> Copy Link
                  </Button>
                  <Button size="sm" onClick={() => smsClockLink(selectedDriver)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-[10px] h-7" data-testid="detail-sms-clock-link">
                    <Send className="h-3 w-3 mr-1" /> Send SMS
                  </Button>
                </div>
              </div>

              {showTripForm && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-3">
                  <h4 className="text-[10px] font-bold mb-2">Log Trip</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" value={tripForm.date} onChange={e => setTripForm(p => ({ ...p, date: e.target.value }))} className="h-7 text-[10px]" />
                    <Select value={tripForm.vehicle} onValueChange={v => setTripForm(p => ({ ...p, vehicle: v }))}><SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Vehicle" /></SelectTrigger><SelectContent>{VEHICLES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
                    <Input placeholder="Client" value={tripForm.client} onChange={e => setTripForm(p => ({ ...p, client: e.target.value }))} className="h-7 text-[10px]" />
                    <Input type="number" placeholder="Hours" value={tripForm.hours || ''} onChange={e => setTripForm(p => ({ ...p, hours: parseFloat(e.target.value) || 0 }))} className="h-7 text-[10px]" />
                    <Input type="number" placeholder="Revenue $" value={tripForm.revenue || ''} onChange={e => setTripForm(p => ({ ...p, revenue: parseFloat(e.target.value) || 0 }))} className="h-7 text-[10px]" />
                    <Input type="number" placeholder="Payout $" value={tripForm.payout || ''} onChange={e => setTripForm(p => ({ ...p, payout: parseFloat(e.target.value) || 0 }))} className="h-7 text-[10px]" />
                  </div>
                  <div className="flex gap-2 mt-2"><Button size="sm" onClick={logTrip} className="bg-emerald-600 text-[10px] h-6 px-2">Save</Button><Button size="sm" variant="outline" onClick={() => setShowTripForm(false)} className="text-[10px] h-6 px-2">Cancel</Button></div>
                </div>
              )}

              <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-2">Trip History</h4>
              {(selectedDriver.trips || []).length === 0 ? (
                <p className="text-[10px] text-gray-400">No trips logged</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(selectedDriver.trips || []).slice().reverse().map((t, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-[10px]">
                      <div><span className="font-semibold">{t.date}</span> <span className="text-gray-500">{t.vehicle}</span></div>
                      <div className="text-right"><span className="font-semibold text-red-600">{f(t.payout)}</span> <span className="text-gray-400">({t.hours}hrs)</span></div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-6 border-gray-200 text-center"><User className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-xs text-gray-400">Select a driver to view details</p></Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverRosterTab;
